import type { interfaces } from 'inversify';
import type ConfigService from './config/ConfigService';
import { PEER_EVENTS } from './constants/events';
import type { CallState } from './engine/CallState';
import type LocalMutex from './engine/LocalMutex';
import type Media from './engine/media';
import type ProducerCloseManager from './engine/media/producer/ProducerCloseManager';
import { ASDKTrackProcessor } from './engine/media/streamEffects/audio/asdk/ASDKTrackProcessor';
import type { AudioTrackProcessor } from './engine/media/streamEffects/audio/AudioTrackProcessor';
import RNNoiseTrackProcessor from './engine/media/streamEffects/audio/noiseSuppression/RNNoiseTrackProcessor';
import type { ProcessorsCache } from './engine/media/streamEffects/ProcessorsCache';
import DefaultAudioTrack, { type AudioTrackDependenciesParams } from './engine/media/tracks/DefaultAudioTrack';
import DefaultVideoTrack, { type VideoTrackDependenciesParams } from './engine/media/tracks/DefaultVideoTrack';
import type MediaStreamTrackManager from './engine/media/tracks/MediaStreamTrackManager';
import PeerTrack, { type PeerTrackDependencies } from './engine/media/tracks/PeerTrack';
import type { MyPeer } from './engine/MyPeer';
import type Network from './engine/network';
import Peer, { type PeerDependencies } from './engine/Peer';
import PeerConsumer, { type PeerConsumerDependencies } from './engine/PeerConsumer';
import type { CreatePeerParams, Peers } from './engine/Peers';
import type { RemotePeerTracks } from './engine/RemotePeerTracks';
import type StatsHandler from './engine/WebRTCStats/StatsHandler';
import type EnhancedEventEmitter from './EnhancedEventEmitter';
import { TOKEN } from './inversify.tokens';
import type {
  AudioTrackFactory,
  AudioTrackProcessorFactory,
  CreateAudioTrackProcessorParams,
  CreatePeerConsumerParams,
  LoggerFactory,
  PeerConsumerFactory,
  PeerFactory,
  PeerTrackFactory,
  PeerTrackFactoryParams,
  VideoTrackFactory,
} from './types/container';
import type { AudioTrackParams, BaseTrackParams } from './types/media';
import type { ScoresCalculator } from './qoe/ScoresCalculator';
import type { WIDStatsAdapter } from './qoe/WIDStatsAdapter';

export function peerTrackFactory(context: interfaces.Context): PeerTrackFactory {
  const loggerFactory = context.container.get<LoggerFactory>(TOKEN.LoggerFactory);
  const callState = context.container.get<CallState>(TOKEN.CallState);
  const statsHandler = context.container.get<StatsHandler>(TOKEN.StatsHandler);

  return (params: PeerTrackFactoryParams): PeerTrack => {
    const deps: PeerTrackDependencies = {
      callState,
      loggerFactory,
      media: context.container.get<Media>(TOKEN.Media),
      network: context.container.get<Network>(TOKEN.Network),
      peerConsumerFactory: context.container.get<PeerConsumerFactory>(TOKEN.PeerConsumerFactory),
      widStatsAdapter: context.container.get<WIDStatsAdapter>(TOKEN.WIDStatsAdapter),
      scoresCalculator: context.container.get<ScoresCalculator>(TOKEN.ScoresCalculator),
    };

    const { peerEventEmitter } = params;

    const peerTrack = new PeerTrack({
      ...params,
      ...deps,
      statsHandler,
    });

    peerEventEmitter.safeEmit(PEER_EVENTS.trackStart, peerTrack);

    return peerTrack;
  };
}

export function peerConsumerFactory(context: interfaces.Context): PeerConsumerFactory {
  const loggerFactory = context.container.get<LoggerFactory>(TOKEN.LoggerFactory);

  return (params: CreatePeerConsumerParams): PeerConsumer => {
    const deps: PeerConsumerDependencies = {
      loggerFactory,
    };

    return new PeerConsumer({
      ...deps,
      ...params,
    });
  };
}

export function peerFactory(context: interfaces.Context): PeerFactory {
  const loggerFactory = context.container.get<LoggerFactory>(TOKEN.LoggerFactory);
  const callState = context.container.get<CallState>(TOKEN.CallState);

  return (params: CreatePeerParams): Peer => {
    const deps: PeerDependencies = {
      callState,
      loggerFactory,
      media: context.container.get<Media>(TOKEN.Media),
      myPeer: context.container.get<MyPeer>(TOKEN.MyPeer),
      network: context.container.get<Network>(TOKEN.Network),
      peers: context.container.get<Peers>(TOKEN.Peers),
      remotePeerTracks: context.container.get<RemotePeerTracks>(TOKEN.RemotePeerTracks),
      peerConsumerFactory: context.container.get<PeerConsumerFactory>(TOKEN.PeerConsumerFactory),
    };

    return new Peer({
      ...deps,
      ...params,
    });
  };
}

export function audioTrackFactory(context: interfaces.Context): AudioTrackFactory {
  const loggerFactory = context.container.get<LoggerFactory>(TOKEN.LoggerFactory);
  const statsHandler = context.container.get<StatsHandler>(TOKEN.StatsHandler);

  return (params: AudioTrackParams): DefaultAudioTrack => {
    const deps: AudioTrackDependenciesParams = {
      internalEventEmitter: context.container.get<EnhancedEventEmitter>(TOKEN.InternalEventEmitter),
      loggerFactory,
      media: context.container.get<Media>(TOKEN.Media),
      mediaStreamTrackManager: context.container.get<MediaStreamTrackManager>(TOKEN.MediaStreamTrackManager),
      myPeer: context.container.get<MyPeer>(TOKEN.MyPeer),
      network: context.container.get<Network>(TOKEN.Network),
      producerCloseManager: context.container.get<ProducerCloseManager>(TOKEN.ProducerCloseManager),
      mutex: context.container.get<LocalMutex>(TOKEN.LocalMutex),
      processorFactory: context.container.get<AudioTrackProcessorFactory>(TOKEN.AudioTrackProcessorFactory),
      clientEventEmitter: context.container.get<EnhancedEventEmitter>(TOKEN.ClientEventEmitter),
      statsHandler,
      widStatsAdapter: context.container.get<WIDStatsAdapter>(TOKEN.WIDStatsAdapter),
      scoresCalculator: context.container.get<ScoresCalculator>(TOKEN.ScoresCalculator),
    };

    return new DefaultAudioTrack({
      ...deps,
      ...params,
    });
  };
}

export function videoTrackFactory(context: interfaces.Context): VideoTrackFactory {
  const loggerFactory = context.container.get<LoggerFactory>(TOKEN.LoggerFactory);
  const statsHandler = context.container.get<StatsHandler>(TOKEN.StatsHandler);

  return (params: BaseTrackParams): DefaultVideoTrack => {
    const deps: VideoTrackDependenciesParams = {
      internalEventEmitter: context.container.get<EnhancedEventEmitter>(TOKEN.InternalEventEmitter),
      loggerFactory,
      media: context.container.get<Media>(TOKEN.Media),
      mediaStreamTrackManager: context.container.get<MediaStreamTrackManager>(TOKEN.MediaStreamTrackManager),
      myPeer: context.container.get<MyPeer>(TOKEN.MyPeer),
      network: context.container.get<Network>(TOKEN.Network),
      producerCloseManager: context.container.get<ProducerCloseManager>(TOKEN.ProducerCloseManager),
      mutex: context.container.get<LocalMutex>(TOKEN.LocalMutex),
      clientEventEmitter: context.container.get<EnhancedEventEmitter>(TOKEN.ClientEventEmitter),
      statsHandler,
      widStatsAdapter: context.container.get<WIDStatsAdapter>(TOKEN.WIDStatsAdapter),
      scoresCalculator: context.container.get<ScoresCalculator>(TOKEN.ScoresCalculator),
    };

    return new DefaultVideoTrack({
      ...deps,
      ...params,
    });
  };
}

export function audioTrackProcessorFactory(context: interfaces.Context): AudioTrackProcessorFactory {
  const loggerFactory = context.container.get<LoggerFactory>(TOKEN.LoggerFactory);
  const config = context.container.get<ConfigService>(TOKEN.Config);
  const processorsCache = context.container.get<ProcessorsCache>(TOKEN.ProcessorsCache);
  const asdk = config.get('asdk');

  return (params: CreateAudioTrackProcessorParams): AudioTrackProcessor => {
    const denoiser = config.get('denoiser');

    let processor = processorsCache.get(denoiser, params.trackLabel);

    if (processor) {
      return processor;
    }

    if (denoiser === 'rnnoise' && params.noiseSuppressor) {
      processor = new RNNoiseTrackProcessor({
        noiseSuppressor: params.noiseSuppressor,
        loggerFactory,
      });
    }

    if (denoiser === 'asdk' && asdk?.customerId) {
      processor = new ASDKTrackProcessor({
        asdkCustomerId: asdk.customerId,
        loggerFactory,
        config,
      });
    }

    if (!processor) {
      throw new TypeError('Incorrect parameters passed to AudioTrackProcessorFactory');
    }

    return processor;
  };
}
